import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Modal, Upload, Button as AntButton } from 'antd';
import { UploadOutlined, DeleteOutlined, PaperClipOutlined,LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { ThemeProps } from '../../genericComponents/globalStyles';
import headerBackground from '../../../assets/images/headerBackgroundYellow.png';
import { Dropdown } from 'semantic-ui-react';
import { CharacterCounter } from '../../genericComponents/CharacterCounter';
import { toast } from 'react-toastify';

const maxDescriptionChar = 500
const schema = yup.object().shape({
  title: yup.string().required().max(200, 'Title must be at most 200 characters'),
  description: yup.string().required().max(maxDescriptionChar, `Summary must be at most ${maxDescriptionChar} characters`),
  categories: yup
    .array()
    .required('Category is a required field'),
  audiences: yup
    .array()
    .required('Audience is a required field'),
  mediaTypes: yup
    .array()
    .required('Media Type is a required field'),
  website: yup.string().required().url('Must be a valid website')
});

const initialValues = {
  title: '',
  description: '',
  categories: '',
  audiences: '',
  mediaTypes: '',
  website: '',
};

const AddContentModal = ({
  isShow,
  onHide,
  addNewContent,
  savingContent,
  categories,
  mediaTypes,
  audiences }) => {
  const [numberCharacters, setNumberCharacters] = useState(0);
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (isShow) {
      setFieldValue("title", '')
      setFieldValue("description", '')
      setFieldValue("categories", '')
      setFieldValue("audiences", '')
      setFieldValue("mediaTypes", '')
      setFieldValue("website", '')
      setFile(null)
    }
  }, [isShow])

  const mapCallback = value => ({ id: value.value, name: value.text });

  const filterCallback = array => value => array.includes(value.value);

  const onSubmit = (newValue) => {

    const audiencesObject = audiences
      .filter(filterCallback(newValue.audiences))
      .map(mapCallback)

    const categoriesObject = categories
      .filter(filterCallback(newValue.categories))
      .map(mapCallback)

    const mediaTypesObject = mediaTypes
      .filter(filterCallback(newValue.mediaTypes))
      .map(mapCallback)

    const newContent = {
      mediaTypes: mediaTypesObject,
      website: newValue.website,
      categories: categoriesObject,
      audiences: audiencesObject,
      description: newValue.description,
      title: newValue.title,
    };
    addNewContent(newContent, file);
  }
  const handleWebsiteChange = (e) => {
    if (e.target.value !== 'https:/') {
      const domain = e.target.value.replace(/^https?\:\/\//i, "")
      e.target.value = `https://${domain}`
    } else {
      e.target.value = ''
    }
    handleChange(e)
  }
  const {
    errors,
    handleChange,
    handleSubmit,
    values,
    setFieldValue,
    touched,
    setFieldTouched,
  } = useFormik({
    initialValues: { ...initialValues },
    validationSchema: schema,
    onSubmit: onSubmit,
  });

  const fileUploadBefore = (file) => {
    if (file.size / 1024 / 1024 <= 5) {
      setFile(file);
    } else {
      toast.error('You are uploading an file larger than 5Mb!');
    }
    return false;
  }

  return (
    <ModalContainer
      visible={isShow}
      width={900}
      footer={null}
      onCancel={onHide}>
      <FormContainer onSubmit={handleSubmit}>
        <ModalHeader>
          <Titles fontSize={"2em"}>Find something interesting?</Titles>
          <LineBottomYellow />
        </ModalHeader>
        <Container>
          <Titles>Title<ErrorText as={"span"}>*</ErrorText></Titles>
          <Input
            name="title"
            type="text"
            placeholder="Title"
            value={values.title}
            onChange={handleChange}
            onBlur={() => setFieldTouched('title', true)}
          />
          {touched.title && errors.title && (
            <ErrorText>{errors.title}</ErrorText>
          )}
          <Divider />
          <Titles>Bite Sized Bit<ErrorText as={"span"}>*</ErrorText></Titles>
          <TextArea
            name="description"
            type="text"
            placeholder="Give us a short summary of what this resource is about"
            value={values.description}
            onChange={(e) => {
              setNumberCharacters(e.target.value.length)
              handleChange(e)
            }}
          />
          <CharacterCounter
            maxNumberCharacters={maxDescriptionChar}
            numberCharacters={numberCharacters} />

          {touched.description && errors.description && (
            <ErrorText>{errors.description}</ErrorText>
          )}
          <Divider />
          <Titles>Categories<ErrorText as={"span"}>*</ErrorText></Titles>
          <CustomDropdown
            placeholder="Select Categories"
            fluid
            search
            selection
            multiple
            options={categories}
            onChange={(_, data) => setFieldValue('categories', data.value)}
            onBlur={() => setFieldTouched('categories', true)}
            value={values.categories}
          />
          {touched.categories && errors.categories && (
            <ErrorText>{errors.categories}</ErrorText>
          )}
          <Divider />
          <Titles>Audiences<ErrorText as={"span"}>*</ErrorText></Titles>
          <CustomDropdown
            placeholder="Select Audiences"
            fluid
            search
            selection
            multiple
            options={audiences}
            onChange={(_, data) => setFieldValue('audiences', data.value)}
            onBlur={() => setFieldTouched('audiences', true)}
            value={values.audiences}
          />
          {touched.audiences && errors.audiences && (
            <ErrorText>{errors.audiences}</ErrorText>
          )}

          <Divider />
          <Titles>Media Types<ErrorText as={"span"}>*</ErrorText></Titles>
          <CustomDropdown
            placeholder="Select Media Types"
            fluid
            search
            selection
            multiple
            options={mediaTypes}
            onChange={(_, data) => setFieldValue('mediaTypes', data.value)}
            onBlur={() => setFieldTouched('mediaTypes', true)}
            value={values.mediaTypes}
          />
          {touched.mediaTypes && errors.mediaTypes && (
            <ErrorText>{errors.mediaTypes}</ErrorText>
          )}
          <Divider />
          <Titles>Website<ErrorText as={"span"}>*</ErrorText></Titles>
          <Input
            type="text"
            value={values.website}
            name="website"
            placeholder="https://"
            onChange={handleWebsiteChange}
          />
          {touched.website && errors.website && (
            <ErrorText>{errors.website}</ErrorText>
          )}
          <Divider />

          <Upload
            accept={"image/png, image/jpeg"}
            beforeUpload={fileUploadBefore}
            onRemove={(file) => setFile(null)}
            showUploadList={false}>
            <AntButton icon={<UploadOutlined />}>Cover Image (Max: 1)</AntButton>
          </Upload>
          {file ?
            (<FileList>
              <div><PaperClipOutlined />{file.name}</div>
              <DeleteOutlined onClick={() => setFile(null)} />
            </FileList>) : null}
          <Divider />
          <Row100>
            <Button onClick={onHide} color={ThemeProps.color.darkBlue} >Cancel</Button>
            <Button
              type="submit"
              disabled={savingContent}
              color={(Object.keys(errors).length > 0) ? ThemeProps.color.white : ThemeProps.color.darkBlue}
              backgroundColor={(Object.keys(errors).length > 0) ? ThemeProps.color.darkBlue : ThemeProps.color.gabaYellow}>
              {savingContent ? (<Saving><div><LoadingOutlined /><span>Saving...</span></div></Saving>) : "Save"}
            </Button>
          </Row100>
        </Container>

      </FormContainer>
    </ModalContainer >
  );
}

export default AddContentModal
const Button = styled.button`
  width: 49%;
  padding: 14px 20px;
  font-size: 1.3em;
  margin-top: 8px;
  border-radius: 6px;
  background-color: ${p => p.backgroundColor};
  color: ${p => p.color};
  border: none;
  outline: none;
`;
const Saving = styled.div`
div{
  width:60%;
  margin:0 auto;
  display:flex;
  justify-content:space-evenly;
}
`

const Row100 = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FileList = styled.div`
display:flex;
flex-direction:row;
justify-content:space-between;
span{
  margin: 0px 10px;
}
`
const inputFontStyle = `
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.1px;
`;

const CustomDropdown = styled(Dropdown)`
  height: 35px;
  border: 1px solid lightgray;
  border-radius: 5px;
  margin-right: 15px;
  padding-left: 10px;
  width: 100%;
  ${inputFontStyle}
`;

const ModalHeader = styled.div`
  width: 100%;
  padding: 20px 16%;
  margin-bottom: 24px;
  background-image: url(${headerBackground});
  background-position-x: -50px;
  background-color: #fefefe;
  border-bottom: 1px solid #ddd;
`;
const Container = styled.div`
  width: 100%;
  padding: 16px 16%;
`;
const LineBottomYellow = styled.div`
  width: 100%;
  border-bottom: 3px solid ${ThemeProps.color.gabaYellow};
  margin: 12px 0;
`;
const Divider = styled.div`
  width: 100%;
  border-bottom: 3px solid #eee;
  margin: 16px 0;
`;
const Titles = styled.label`
  font-size: ${p => p.fontSize || "1.4em"};
`;
const FormContainer = styled.form`
  display:flex;
  flex-direction: column;
  width: 100%;
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 130px;
  resize: vertical;
  border-radius: 6px;
  border-color: #ccc;
  font-size: 1.4em;
  padding: 12px 12px;
  margin: 8px 0 0 0;
  outline-color: #aaa;
  background-color: #fbfbfb;
  border: 2.5px solid #ddd;
`;
const Input = styled.input`
  width: 100%;
  margin: 8px 0 0 0;
  font-size: 1.3em;
  padding: 10px 12px;
  border-radius: 4px;
  outline-color: #aaa;
  background-color: #fbfbfb;
  border: 2.5px solid #ddd;
`;

const ErrorText = styled.p`
  color: #F00;
  margin: 8px 0;
`;

const ModalContainer = styled(Modal)`
.ant-modal-footer{
  display:flex;
  justify-content:space-evenly;
}
`